import { getHighlights } from 'actions'
import { Shared } from 'components'
import { useQuery } from 'hooks'
import { debounce, mapValues } from 'lodash'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Popup from 'reactjs-popup'
import { renderCell } from 'utilities'
import '../styles.scss'
import { highlightsColumns } from 'config/constants'

export const HighlightsGrid = ({ dates }) => {
  const dispatch = useDispatch()
  const { sort: sortParam = '{}', filter: filterParam = '{}' } = useQuery()
  const searchQuery = useMemo(
    () => ({
      sortBy: sortParam !== '{}' ? mapValues(JSON.parse(sortParam), (value) => Number(value)) : undefined,
      ...JSON.parse(filterParam),
    }),
    [sortParam, filterParam]
  )

  const { loading, docs, page, hasNextPage, nextPage } = useSelector(({ app }) => app?.highlightsList)

  const fetch = useCallback(
    ({ getStats, onSuccess, ...payload } = {}) => dispatch(getHighlights({ payload, getStats, onSuccess })),
    [dispatch]
  )
  const fetchDebounced = debounce(fetch, 300)

  const handlePagination = () =>
    hasNextPage &&
    !loading &&
    fetchDebounced({
      pageNumber: nextPage,
      ...searchQuery,
      ...(dates?.startDate && { startDate: dates?.startDate.format('YYYY-MM-DD') }),
      ...(dates?.endDate && { endDate: dates?.endDate.format('YYYY-MM-DD') }),
    })

  useEffect(() => {
    fetch({
      ...searchQuery,
      ...(dates?.startDate && { startDate: dates?.startDate.format('YYYY-MM-DD') }),
      ...(dates?.endDate && { endDate: dates?.endDate.format('YYYY-MM-DD') }),
    })
  }, [fetch, searchQuery, dates])

  const availableSort = highlightsColumns.map(({ value }) => value).filter((v) => ![].includes(v))
  const availableFilters = useMemo(
    () => ({
      event: { type: 'regex' },
      user: { type: 'regex' },
      paid: { type: 'boolean' },
    }),
    []
  )
  const keyMap = { styles: 'combatStyles', startDate: 'date' }
  const sortingComponents = highlightsColumns
    ?.filter(({ sortable }) => sortable)
    .reduce(
      (acc, { value }) => ({
        ...acc,
        [value]: (
          <Popup
            trigger={
              <div className="icon icon-arrow" style={{ width: '1.2rem', height: '1.2rem', marginLeft: '5px' }} />
            }
            contentStyle={{ width: 'auto' }}
          >
            {(close) => (
              <Shared.SortingComponent
                close={close}
                column={value}
                availableSort={availableSort}
                availableFilters={availableFilters}
                keyMap={keyMap}
              />
            )}
          </Popup>
        ),
      }),
      {}
    )

  return (
    <div className="screen-activities-content" style={{ height: 'calc(100% - 5%)' }}>
      {loading && !page ? (
        <Shared.LoaderInline />
      ) : (
        <Shared.Table
          fixedWidth
          columns={highlightsColumns}
          data={docs}
          handlePagination={handlePagination}
          sortingComponent={sortingComponents}
          renderCell={renderCell.highlights}
          loader={loading}
        />
      )}
    </div>
  )
}
